import { z } from 'zod/v4';
import { CAPA_ACTION_STATUS } from '../../constants.js';

export const ImportSeveritySchema = z.enum(['A', 'B', 'C', 'D', 'E']);

// All six statuses stay accepted: the AI review UI offers only five (the CAP
// extraction can never emit CHANGES_REQUESTED), but other callers and
// re-imports legitimately carry it. The cast keeps the inferred type `string`.
const capaActionStatusValues = Object.values(CAPA_ACTION_STATUS) as [
  string,
  ...string[],
];

export const ImportActionSchema = z.object({
  title: z.string().min(1, 'Action title is required'),
  description: z.string().min(1, 'Action description is required'),
  type: z.string().min(1, 'Action type is required'),
  status: z.enum(capaActionStatusValues).optional(),
  dueDate: z.string().optional(),
  severity: ImportSeveritySchema.optional(),
  impactAreaId: z.string().optional(),
  impactAreaExtractedName: z.string().optional(),
  assignee: z
    .object({
      id: z.string().min(1, 'Assignee user ID is required'),
      companyId: z.string().min(1, 'Assignee company ID is required'),
    })
    .nullable()
    .optional(),
  assigneeExtractedName: z.string().optional(),
  collaborator: z
    .object({
      companyId: z.string().min(1, 'Collaborator company ID is required'),
    })
    .nullable()
    .optional(),
  collaboratorExtractedName: z.string().optional(),
  reporters: z
    .array(
      z.object({
        userId: z.string().min(1, 'Reporter user ID is required'),
        companyId: z.string().min(1, 'Reporter company ID is required'),
      }),
    )
    .optional(),
});
export type ImportAction = z.infer<typeof ImportActionSchema>;

export const ImportFindingSchema = z.object({
  title: z.string().min(1, 'Finding title is required'),
  description: z.string().min(1, 'Finding description is required'),
  severity: ImportSeveritySchema.optional(),
  impactAreaId: z.string().optional(),
  impactAreaExtractedName: z.string().optional(),
  actions: z.array(ImportActionSchema),
});
export type ImportFinding = z.infer<typeof ImportFindingSchema>;

export const ImportActionsRequestSchema = z.object({
  sourceType: z.string().min(1, 'Source type is required'),
  /** The audit the findings belong to; also the actions' source id. */
  sourceId: z.string().min(1, 'Source ID is required'),
  /** The responsible (OWNER) company of every imported action. */
  companyId: z.string().min(1, 'Company ID is required'),
  findings: z
    .array(ImportFindingSchema)
    .min(1, 'At least one finding is required'),
  // Optional CAPA-workflow linkage used by the AI-processing review jobs
  // (CAP extraction review / findings review) to make imports idempotent.
  workflowId: z.string().min(1).optional(),
  reviewStage: z.enum(['CAP', 'FINDINGS']).optional(),
});
export type ImportActionsRequest = z.infer<typeof ImportActionsRequestSchema>;

export const ImportActionsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  createdFindings: z.number(),
  createdActions: z.number(),
  actionIds: z.array(z.string()),
});
export type ImportActionsResponse = z.infer<typeof ImportActionsResponseSchema>;
